/**
 * FreeCreate provider / capability 派生 hook。
 *
 * 输入: 当前 raw form state(provider/ratio/tier/count + thinking)
 * 输出: enabledProviders / supportedRatios / supportedTiers / valid* 派生值 / constraints。
 *
 * V26 Wave 5 派生哲学: state 永远存"用户最后一次显式选择",渲染态从 constraints 派生
 * valid* 永远合法。提交时用 valid* 而不是 raw state。
 */
import { useEffect, useMemo } from "react";

import { useProviderCapabilities } from "@/entities/media-provider";
import { useProviderConstraints } from "@/entities/media-provider";
import { useCompliance } from "@/contexts/AuthContext";
import { FOREIGN_IMAGE_PROVIDERS } from "../domesticProviders";
import { engineLabel } from "@/shared/brand/engineTiers";
import type { Provider, ResolutionTier } from "../../api/posterService";

import type { Count, Ratio } from "./useFreeCreateForm";

// Phase D (2026-05-16) — RATIOS 不再带 sizePreset(wire 已切到 ratio + tier 双维度)。
// label / desc / w / h 仍是 UI 用。
export const RATIOS: ReadonlyArray<{
  value: Ratio;
  label: string;
  tag: string;
  desc: string;
  w: number;
  h: number;
}> = [
  { value: "1:1", label: "方图", tag: "1:1", desc: "朋友圈 / 微信群", w: 56, h: 56 },
  { value: "4:5", label: "近方", tag: "4:5", desc: "小红书 / Instagram", w: 48, h: 60 },
  { value: "3:4", label: "竖图", tag: "3:4", desc: "抖音封面", w: 48, h: 64 },
  { value: "9:16", label: "长图", tag: "9:16", desc: "公众号长图 / 短视频", w: 36, h: 64 },
  { value: "16:9", label: "横版", tag: "16:9", desc: "YouTube / 横版 banner", w: 64, h: 36 },
];

// V7 (2026-05-12) — 分辨率档位 chip 元数据。label 简短 hint 详细。
export const RESOLUTION_TIERS: ReadonlyArray<{
  value: ResolutionTier;
  label: string;
  hint: string;
}> = [
  { value: "1K", label: "1K", hint: "~1024px · 快速预览" },
  { value: "2K", label: "2K", hint: "~2048px · 网络发布(推荐)" },
  { value: "3K", label: "3K", hint: "~3072px · 高清(极速档独有)" },
  { value: "4K", label: "4K", hint: "~3840px · 打印 / 大屏" },
];

/**
 * 出图档位 chip。★OEM 0929(C-C):label 走 engineLabel 档位名(极速 / 精绘 / 标准 / 高清 /
 * 旗舰·极速 / 旗舰·精修 / 创意),不再写「豆包 SeaDream」「GPT Image」这类厂商真名。
 * label 用 getter:超管非代客态(vendorMask reveal)在渲染时才附真名,模块加载时的开关值不作数。
 */
function providerOption(
  value: Provider,
  desc: string
): { value: Provider; label: string; desc: string } {
  return {
    value,
    get label() {
      return engineLabel("image", value);
    },
    desc,
  };
}

export const PROVIDERS: ReadonlyArray<{ value: Provider; label: string; desc: string }> = [
  providerOption("DOUBAO", "速度快 · 默认"),
  providerOption("WANXIANG", "细节细 · 唯一支持局部重绘"),
  providerOption("OPENAI", "普通档 · 默认"),
  providerOption("GOOGLE", "新潮"),
  // 0831 终版定价:出图双档。VIP 是**独立渠道条目**(ai capabilities 的 OPENAI_VIP,
  // 报价规格档 openai_vip_{1K,2K,4K}),不是 OPENAI 的一个开关 —— 报价/结算按渠道×档位
  // 查表,两档必须是两个 provider 才能各自对上自己的价。
  // ★generated `Provider` union 还没有 OPENAI_VIP(backend OpenAPI 未重导),cast 过桥
  //   (仓内先例:TaskDto.requestedCount 同法);codegen 跟上后此 cast 自然消解。
  providerOption("OPENAI_VIP" as Provider, "高质量档"),
  // 0910: 旗舰双模型。同 VIP:独立渠道条目,
  // 报价规格档 openai_flare_* / openai_sunburst_*;generated union 未重导,cast 过桥。
  providerOption("OPENAI_FLARE" as Provider, "新旗舰 · 出图快"),
  providerOption("OPENAI_SUNBURST" as Provider, "新旗舰 · 细节最好"),
];

/**
 * 只有 capabilities 里真有条目才展示的渠道(新渠道灰度保护):
 * 旧渠道条目在 ai 侧恒存在,`capabilities[p.value]?.enabled !== false` 的「缺条目=展示」
 * 兜底对它们是安全的;OPENAI_VIP 若对着**还没发 VIP 的 ai** 也走这条兜底,就会出现
 * 一个选了必 4xx 的死选项 —— 缺条目时宁可不展示。
 */
const CAPABILITY_GATED: ReadonlySet<string> = new Set([
  "OPENAI_VIP",
  "OPENAI_FLARE",
  "OPENAI_SUNBURST",
]);

export interface FreeCreateConstraints {
  capabilities: ReturnType<typeof useProviderCapabilities>["data"] extends infer T
    ? T extends { providers: infer P }
      ? P | undefined
      : undefined
    : undefined;
  enabledProviders: typeof PROVIDERS;
  supportedRatios: typeof RATIOS;
  supportedTiers: ReadonlyArray<ResolutionTier>;
  validRatio: Ratio;
  validTier: ResolutionTier | null;
  validCount: Count;
  constraints: ReturnType<typeof useProviderConstraints>;
  /**
   * 0926:能力表拉取失败且手里没有可用数据 → 错误对象;否则 null。
   * 出图页据此禁用「生成」、底栏显示「价格待系统核算」(档位/渠道全靠它,没有就报不出价)。
   */
  capabilitiesError: unknown;
}

interface Args {
  provider: Provider;
  setProvider: (v: Provider) => void;
  ratio: Ratio;
  resolutionTier: ResolutionTier | null;
  count: Count;
  useGptThinking: boolean;
}

export function useFreeCreateConstraints({
  provider,
  setProvider,
  ratio,
  resolutionTier,
  count,
  useGptThinking,
}: Args): FreeCreateConstraints {
  const capQ = useProviderCapabilities();
  const capabilities = capQ.data?.providers;
  const { domesticOnly } = useCompliance();

  // V24 — 按 capabilities 过滤可选 provider + ratio
  const enabledProviders = useMemo(() => {
    // 1011 domesticOnly:只列能力接口真返回且 enabled 的国内渠道(缺条目 = 不展示,不再兜底)
    if (domesticOnly) {
      return PROVIDERS.filter((p) => {
        if (FOREIGN_IMAGE_PROVIDERS.has(p.value)) return false;
        if (!capabilities) return !CAPABILITY_GATED.has(p.value);
        const cap = capabilities[p.value];
        return !!cap && cap.enabled !== false;
      });
    }
    // capabilities 未到货:只出非 gated 渠道(gated 渠道没有条目就当不存在,见 CAPABILITY_GATED)
    if (!capabilities) return PROVIDERS.filter((p) => !CAPABILITY_GATED.has(p.value));
    return PROVIDERS.filter((p) => {
      const cap = capabilities[p.value];
      if (!cap) return !CAPABILITY_GATED.has(p.value);
      return cap.enabled !== false;
    });
  }, [capabilities, domesticOnly]);

  // V26 (2026-05-14) Wave 5 — 纯派生 state,删 cascade useEffect。
  // 数据依赖链:provider (+ thinking) → ratio/tier/count 的合法子集。
  const constraints = useProviderConstraints(provider, {
    thinking: !domesticOnly && provider === "OPENAI" && useGptThinking,
  });
  const supportedRatios = useMemo(() => {
    if (!capabilities) return RATIOS;
    return RATIOS.filter((r) => constraints.ratios.includes(r.value));
  }, [capabilities, constraints.ratios]);
  const supportedTiers = constraints.tiers;

  // V24: capabilities 加载后,如果当前 provider 已 disabled,自动跳到第一个 enabled
  // (这是 "provider 自身合法性" cascade,不是派生 — provider 是 root,必须强校正)
  useEffect(() => {
    // 1011 domesticOnly:当前渠道(默认 OPENAI / 草稿恢复的境外档)不在可选列表 → 切到列表首个
    if (domesticOnly) {
      const first = enabledProviders[0]?.value;
      if (first && !enabledProviders.some((p) => p.value === provider)) setProvider(first);
      return;
    }
    if (!capabilities) return;
    const cap = capabilities[provider];
    // gated 渠道(OPENAI_VIP)缺条目 = 这个 ai 还不认识它,与 disabled 同判(否则草稿
    // 恢复出一个 VIP 选择却对着老 ai,提交必 4xx 且列表里根本看不见它)
    const curEnabled = cap ? cap.enabled !== false : !CAPABILITY_GATED.has(provider);
    if (curEnabled) return;
    const firstEnabled = enabledProviders[0]?.value;
    if (firstEnabled && firstEnabled !== provider) {
      setProvider(firstEnabled);
    }
  }, [capabilities, provider, enabledProviders, setProvider, domesticOnly]);

  // 派生:从 raw state 计算合法值(用于 chip active + 提交 payload)
  const validRatio: Ratio = useMemo(() => {
    if (constraints.ratios.length === 0) return ratio;
    if (constraints.ratios.includes(ratio)) {
      // 还要看 ratio × tier 组合是否合法(OPENAI 4K + 3:4/4:5 deny)
      if (resolutionTier && !constraints.isCombinationLegal(ratio, resolutionTier)) {
        // 当前组合 deny,但 ratio 本身合法 — 保持 ratio,让 validTier 收
      }
      return ratio;
    }
    return (constraints.defaultRatio ?? constraints.ratios[0]) as Ratio;
  }, [ratio, resolutionTier, constraints]);

  const validTier: ResolutionTier | null = useMemo(() => {
    if (constraints.tiers.length === 0) return resolutionTier;
    // 默认选 1K(2026-06-15):用户未显式选时优先 1K(最便宜/最快,跟分档计费对齐),
    // 1K 不支持才退回 provider defaultResolutionTier。用户在参数弹窗手动改后以其选择为准。
    const fallbackTier = constraints.tiers.includes("1K" as ResolutionTier)
      ? ("1K" as ResolutionTier)
      : (constraints.defaultResolutionTier ?? null);
    const target = resolutionTier ?? fallbackTier;
    if (!target) return null;
    if (!constraints.tiers.includes(target as ResolutionTier)) {
      return (constraints.defaultResolutionTier ?? constraints.tiers[0]) as ResolutionTier;
    }
    // combinationDeny 校验:若 (validRatio, target) 不合法,退回 default
    if (!constraints.isCombinationLegal(validRatio, target as ResolutionTier)) {
      return (constraints.defaultResolutionTier ?? constraints.tiers[0]) as ResolutionTier;
    }
    return target as ResolutionTier;
  }, [resolutionTier, validRatio, constraints]);

  const validCount: Count = useMemo(() => {
    if (constraints.maxCount === 0) return count;
    return Math.min(count, constraints.maxCount) as Count;
  }, [count, constraints.maxCount]);

  return {
    capabilities: capabilities as FreeCreateConstraints["capabilities"],
    enabledProviders,
    supportedRatios,
    supportedTiers,
    validRatio,
    validTier,
    validCount,
    constraints,
    capabilitiesError: capQ.isError && !capabilities ? capQ.error : null,
  };
}
